<script lang="ts">
  /**
   * The optional timer. Stopping does not save — it hands the rounded hours to
   * the quick-entry form so the notes can still be written and the number
   * adjusted before anything is committed.
   */
  import { timer } from '$lib/timer.svelte';
  import { store } from '$lib/store.svelte';
  import { formatHours } from '$shared/dates';

  interface Props {
    onstop?: (result: { hours: number; note?: string }) => void;
  }

  let { onstop }: Props = $props();

  const runningClient = $derived(
    timer.running ? store.clients.find((c) => c.id === timer.running!.clientId) : null,
  );

  function start() {
    if (store.activeClientId) timer.start(store.activeClientId);
  }

  function stop() {
    const result = timer.stop();
    if (result) onstop?.({ hours: result.hours, note: result.note });
  }
</script>

<div class="bar" class:running={!!timer.running}>
  {#if timer.running}
    <div class="info">
      <span class="pulse" aria-hidden="true"></span>
      <div>
        <div class="time num">{timer.display}</div>
        <div class="muted sub">
          {runningClient?.name ?? 'Unknown client'} · rounds to {formatHours(timer.roundedHours)}
        </div>
      </div>
    </div>
    <div class="row">
      <button class="ghost" onclick={() => timer.cancel()}>Discard</button>
      <button class="primary" onclick={stop}>Stop</button>
    </div>
  {:else}
    <span class="muted sub">Timer</span>
    <button onclick={start} disabled={!store.activeClientId}>Start timer</button>
  {/if}
</div>

<style>
  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.6rem 0.85rem;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 14px;
  }

  .bar.running {
    border-color: var(--ok);
    background: color-mix(in srgb, var(--ok) 10%, var(--surface));
  }

  .info {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-width: 0;
  }

  .time {
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.1;
  }

  .sub {
    font-size: 0.8rem;
  }

  .pulse {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--ok);
    flex: none;
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.25;
    }
  }
</style>
